import * as React from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  createSampleShipmentDestination,
  deleteSampleShipmentDestination,
  getSampleShipmentDestinations,
  updateSampleShipmentDestination,
} from "../../../../services/sampleSetupService";
import { showErrorAlert } from "../../../ui/utils/AlertUtils";

const EMPTY_DESTINATION = {
  title: "",
  hospital: "",
  ward: "",
  building: "",
  street: "",
  zone: "",
  floor: "",
  zipcode: "",
  city: "",
  country: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
};

const ADDRESS_FIELDS = [
  { key: "hospital", translationKey: "hospital", fallback: "Hospital" },
  { key: "ward", translationKey: "ward", fallback: "Ward" },
  { key: "building", translationKey: "building", fallback: "Building" },
  { key: "street", translationKey: "street", fallback: "Street" },
  { key: "zone", translationKey: "zone", fallback: "Zone" },
  { key: "floor", translationKey: "floor", fallback: "Floor" },
  { key: "zipcode", translationKey: "zipcode", fallback: "Zip Code" },
  { key: "city", translationKey: "city", fallback: "City" },
  { key: "country", translationKey: "country", fallback: "Country" },
];

const CONTACT_FIELDS = [
  { key: "contactName", translationKey: "name", fallback: "Name" },
  {
    key: "contactPhone",
    translationKey: "phoneNumber",
    fallback: "Phone Number",
  },
  { key: "contactEmail", translationKey: "email", fallback: "Email" },
];

const addressSummary = (destination) => {
  const address = [
    destination.hospital,
    destination.ward,
    destination.building,
    destination.street,
    destination.zone,
    destination.floor,
    destination.zipcode,
    destination.city,
    destination.country,
  ]
    .filter(Boolean)
    .join(", ");
  const contact = [
    destination.contactName,
    destination.contactPhone,
    destination.contactEmail,
  ]
    .filter(Boolean)
    .join(" · ");

  return { address, contact };
};

const DestinationEditor = ({
  destination,
  disabled,
  onChange,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const updateField = (field, value) =>
    onChange((currentDestination) => ({
      ...currentDestination,
      [field]: value,
    }));

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: "grey.50" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            autoFocus
            required
            fullWidth
            size="small"
            label={t("destinationTitle", "Title")}
            value={destination.title}
            disabled={disabled}
            onChange={(event) => updateField("title", event.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("destinationAddress", "Destination Address")}
          </Typography>
        </Grid>
        {ADDRESS_FIELDS.map((field) => (
          <Grid item xs={12} md={6} key={field.key}>
            <TextField
              fullWidth
              size="small"
              label={t(field.translationKey, field.fallback)}
              value={destination[field.key] || ""}
              disabled={disabled}
              onChange={(event) => updateField(field.key, event.target.value)}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
            {t("destinationContact", "Destination Contact")}
          </Typography>
        </Grid>
        {CONTACT_FIELDS.map((field) => (
          <Grid item xs={12} md={6} key={field.key}>
            <TextField
              fullWidth
              size="small"
              type={field.key === "contactEmail" ? "email" : "text"}
              label={t(field.translationKey, field.fallback)}
              value={destination[field.key] || ""}
              disabled={disabled}
              onChange={(event) => updateField(field.key, event.target.value)}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              disabled={disabled}
              onClick={onCancel}>
              {t("cancel", "Cancel")}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={disabled}
              onClick={onSave}>
              {t("save", "Save")}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export const DestinationAddressSettings = ({ studyId }) => {
  const { t } = useTranslation();
  const [destinations, setDestinations] = React.useState([]);
  const [draft, setDraft] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);

  const loadDestinations = React.useCallback(async () => {
    if (!studyId) {
      setDestinations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const list = await getSampleShipmentDestinations(studyId);
      setDestinations(list || []);
    } catch (error) {
      console.error("Failed to load sample destinations:", error);
      showErrorAlert(
        t(
          "sampleShipmentDestinationsLoadFailed",
          "Failed to load destinations.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [studyId, t]);

  React.useEffect(() => {
    loadDestinations();
  }, [loadDestinations]);

  const startCreate = () => {
    setDraft({ ...EMPTY_DESTINATION, id: null });
  };

  const startEdit = (destination) => {
    setDraft({ ...EMPTY_DESTINATION, ...destination });
  };

  const saveDestination = async () => {
    const title = draft?.title?.trim();
    if (!title) {
      showErrorAlert(t("destinationTitleRequired", "Title is required."));
      return;
    }

    setIsSaving(true);
    try {
      const payload = { ...draft, title, studyId };
      const savedDestination = draft.id
        ? await updateSampleShipmentDestination(draft.id, payload)
        : await createSampleShipmentDestination(payload);

      setDestinations((currentDestinations) =>
        draft.id
          ? currentDestinations.map((destination) =>
              String(destination.id) === String(draft.id)
                ? savedDestination
                : destination,
            )
          : [...currentDestinations, savedDestination],
      );
      setDraft(null);
    } catch (error) {
      console.error("Failed to save sample destination:", error);
      showErrorAlert(
        t(
          "sampleShipmentDestinationSaveFailed",
          "Failed to save destination.",
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const removeDestination = async (destination) => {
    const result = await Swal.fire({
      text: t(
        "sampleShipmentDestinationDeleteConfirmation",
        "Are you sure you want to remove this destination?",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete", "Delete"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#d32f2f",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setIsSaving(true);
    try {
      await deleteSampleShipmentDestination(destination.id, studyId);
      setDestinations((currentDestinations) =>
        currentDestinations.filter(
          (currentDestination) =>
            String(currentDestination.id) !== String(destination.id),
        ),
      );
      if (String(draft?.id) === String(destination.id)) {
        setDraft(null);
      }
    } catch (error) {
      console.error("Failed to delete sample destination:", error);
      showErrorAlert(
        t(
          "sampleShipmentDestinationDeleteFailed",
          "Failed to delete destination.",
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 2, borderRadius: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}>
          <Box>
            <Typography variant="h6">
              {t("destinationAddress", "Destination Address")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                "destinationAddressListHelp",
                "The first destination is used by default on new sample shipments.",
              )}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={isSaving || Boolean(draft)}
            onClick={startCreate}>
            {t("add", "Add")}
          </Button>
        </Box>

        {draft && (
          <DestinationEditor
            destination={draft}
            disabled={isSaving}
            onChange={setDraft}
            onCancel={() => setDraft(null)}
            onSave={saveDestination}
          />
        )}

        <Divider sx={{ my: 2 }} />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : destinations.length === 0 ? (
          <Typography color="text.secondary">
            {t("noDestinations", "No destination addresses configured.")}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {destinations.map((destination, index) => {
              const summary = addressSummary(destination);

              return (
                <Grid item xs={12} md={6} key={destination.id}>
                  <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                      }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {destination.title}
                          </Typography>
                          {index === 0 && (
                            <Typography variant="caption" color="primary.main">
                              {t("default", "Default")}
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.75 }}>
                          {summary.address ||
                            t("addressNotCompleted", "Address not completed")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5, fontWeight: 600 }}>
                          {summary.contact ||
                            t("contactNotCompleted", "Contact not completed")}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          aria-label={t("edit", "Edit")}
                          disabled={isSaving || Boolean(draft)}
                          onClick={() => startEdit(destination)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={t("delete", "Delete")}
                          disabled={isSaving || Boolean(draft)}
                          onClick={() => removeDestination(destination)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
    </Grid>
  );
};
